import { useEffect, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { type Appointment, getAppointments } from 'lib/api/getAppointments';
import { NormalizedLoad } from 'types/Load';
import captureException from 'utils/captureException';

import { AppointmentListCard } from './AppointmentListCard';

type AppointmentsListProps = {
  load: NormalizedLoad;
};

export function AppointmentsList({ load }: AppointmentsListProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllAppointments, setShowAllAppointments] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!load.freightTrackingID) {
        return;
      }

      setIsLoading(true);
      try {
        const result = await getAppointments(load.freightTrackingID);

        if (result.isOk()) {
          setAppointments(result.value.appointments || []);
        } else {
          console.error('Failed to fetch appointments:', result.error);
          toast({
            description: 'Failed to fetch appointments',
            variant: 'destructive',
          });
        }
      } catch (error) {
        captureException(error, { functionName: 'fetchAppointments' });
        toast({
          description: 'Error fetching appointments',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();
  }, [load.freightTrackingID, toast]);

  // Don't render anything if no appointments
  if (!isLoading && appointments.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <div className='my-4'>
        <Typography variant='h5' weight='medium' className='mb-2'>
          Scheduled Appointments
        </Typography>
        <div className='text-sm text-neutral-600'>Loading appointments...</div>
      </div>
    );
  }

  const handleToggleShowAll = () => {
    setShowAllAppointments(!showAllAppointments);
  };

  const displayedAppointments = showAllAppointments
    ? appointments
    : appointments.slice(0, 2);
  const hasMoreAppointments = appointments.length > 2;

  return (
    <div className='my-4'>
      <Typography variant='h5' weight='medium' className='mb-2'>
        Scheduled Appointments
      </Typography>
      <div className='space-y-2'>
        {displayedAppointments.map((appointment) => (
          <AppointmentListCard key={appointment.ID} appointment={appointment} />
        ))}
      </div>

      {hasMoreAppointments && (
        <div className='my-2'>
          <Button
            type='button'
            variant='ghost'
            onClick={handleToggleShowAll}
            buttonNamePosthog='toggle-appointments-view'
            className='w-40 mx-auto h-8 flex items-center justify-center gap-2 border border-transparent hover:border-neutral-600 hover:bg-neutral-200'
          >
            <Typography variant='body-sm' className='text-neutral-600 ml-1'>
              {showAllAppointments ? 'Show less' : 'Show more'}
            </Typography>
            {showAllAppointments ? (
              <ChevronUpIcon className='w-4 h-4' />
            ) : (
              <ChevronDownIcon className='w-4 h-4' />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
